import { useEffect, useState, useMemo } from 'react';
import { useParams, Outlet, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useQueryState, IconLoader, Filters, BaseTransaction } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearCardsStamenetState } from '../store/slices/accounts-statements';
import { overviewAccountsSchema } from '../variables/overview-accounts';
import { CustomerType, StatementsContextType } from '../types';
import AmlStatementsTransaction from '../../../components/partial/aml-statements/aml-statements-transactions';
import formatAmlTransacationList from '../helpers/format-aml-transactions-list';

import cn from '../customers.module.css';

const AccountTransactions = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const {
    accounts,
    individualTransactionsIsLoading,
    corporateTransactionsIsLoading,
    individualTransactionsIsFetching,
    corporateTransactionsIsFetching,
    errorList,
    sendMessage,
    setPage,
  } = useOutletContext<
    StatementsContextType & {
      individualTransactionsIsFetching?: boolean;
      corporateTransactionsIsFetching?: boolean;
      individualTransactionsIsLoading?: boolean;
      corporateTransactionsIsLoading?: boolean;
      errorList?: string;
      sendMessage: SendMessage;
      setPage?: ({
        id,
        take,
        filters,
      }: {
        id: string;
        take: string;
        filters: Filters;
      }) => void;
    }
  >();

  const [accountId, setAccountId] = useState('');

  const dispatch = useAppDispatch();
  const { customerId } = useParams();

  const {
    individualCustomers: {
      info: {
        aml: {
          amlTransactions: individualAmlTransactions,
          pagination: individualPagination,
        },
      },
    },
    corporateCustomers: {
      info: {
        aml: {
          amlTransactions: corporateAmlTransactions,
          pagination: corporatePagination,
        },
      },
    },
    customersDictionaries: { customersTransactionTypes, currenciesList },
  } = useAppSelector((state) => state);

  const { filters, sort } = useQueryState(overviewAccountsSchema);

  useEffect(() => {
    if (!accountId && accounts) {
      setAccountId(accounts[0]?.id);
    }
  }, [accountId, accounts]);

  useEffect(() => {
    dispatch(clearCardsStamenetState());
  }, [dispatch]);

  const formattedData = useMemo(() => {
    const data = formatAmlTransacationList(
      customerType === CustomerType.INDIVIDUAL
        ? (individualAmlTransactions as BaseTransaction[])
        : (corporateAmlTransactions as BaseTransaction[]),
      customerId,
      customerType,
      customersTransactionTypes,
      currenciesList,
    );

    return data;
  }, [individualAmlTransactions, corporateAmlTransactions, customerType]);

  let content;

  if (individualTransactionsIsLoading || corporateTransactionsIsLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <AmlStatementsTransaction
        onSort={() => {}}
        onFilter={() => {}}
        data={formattedData}
        errorList={errorList}
        pagination={
          customerType === CustomerType.INDIVIDUAL
            ? individualPagination
            : corporatePagination
        }
        filters={filters}
        sort={sort}
        requestInProgress={
          individualTransactionsIsFetching || corporateTransactionsIsFetching
        }
        setPage={
          setPage as ({
            id,
            take,
            filters,
          }: {
            id: string;
            take: string;
            filters: Filters;
          }) => void
        }
      />
    );
  }

  return (
    <>
      <div
        className={`${cn.overviewStatementsContentTable} ${
          individualTransactionsIsFetching || corporateTransactionsIsFetching
            ? cn.tableLoading
            : ''
        }`}
      >
        {content}
      </div>
      <Outlet context={{ sendMessage }} />
    </>
  );
};

export default AccountTransactions;
